import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { tenantKeys } from "@/lib/queryKeys";
import {
  darDeBajaTenant,
  getTenantById,
  reactivarTenant,
} from "@/services/platformTenantsService";

/**
 * Root-only card to dar de baja / reactivar a tenant (offboarding). Rendered on the
 * platform tenant-detail page (already root-gated); the backend endpoints enforce root
 * too. Baja stops reportes/recibidas/emisión + pauses billing; data is retained.
 */
export function TenantBajaCard({ tenantId }: { tenantId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");

  const bajaKey = [...tenantKeys.overview(tenantId), "baja"] as const;
  const { data: detail } = useQuery({
    queryKey: bajaKey,
    queryFn: () => getTenantById(tenantId),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: bajaKey });
    void queryClient.invalidateQueries({
      queryKey: tenantKeys.overview(tenantId),
    });
  };

  const bajaMutation = useMutation({
    mutationFn: () => darDeBajaTenant(tenantId, reason.trim()),
    onSuccess: () => {
      toast.success("Empresa dada de baja.");
      setConfirmOpen(false);
      setReason("");
      invalidate();
    },
    onError: () => toast.error("No se pudo dar de baja."),
  });

  const reactivarMutation = useMutation({
    mutationFn: () => reactivarTenant(tenantId),
    onSuccess: () => {
      toast.success("Empresa reactivada.");
      invalidate();
    },
    onError: () => toast.error("No se pudo reactivar."),
  });

  const bajaAt = detail?.bajaAt ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          Baja de empresa
          {bajaAt ? (
            <StatusBadge variant="danger">Dada de baja</StatusBadge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {bajaAt ? (
          <>
            <p className="text-sm text-muted-foreground">
              Dada de baja el{" "}
              {new Date(bajaAt).toLocaleDateString("es-UY")}.
              {detail?.bajaReason ? ` Motivo: ${detail.bajaReason}` : ""}
            </p>
            <p className="text-sm text-muted-foreground">
              No se envían reportes diarios ni se sincronizan recibidas, y no se
              pueden emitir comprobantes. Los datos se conservan.
            </p>
            <Button
              variant="outline"
              onClick={() => reactivarMutation.mutate()}
              disabled={reactivarMutation.isPending}
            >
              Reactivar empresa
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Dar de baja detiene reportes diarios, recibidas y emisión, y pausa la
              facturación (plan gratis). Los datos se conservan — no se elimina nada.
              Usalo cuando el RUC fue dado de baja en DGI.
            </p>
            <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
              Dar de baja
            </Button>
          </>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Dar de baja a la empresa</DialogTitle>
            <DialogDescription>
              Se detienen reportes, recibidas y emisión, y se pausa la facturación.
              Los datos se conservan. Ingresá el motivo (opcional).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo (ej.: RUC dado de baja en DGI)"
            rows={3}
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => bajaMutation.mutate()}
              disabled={bajaMutation.isPending}
            >
              Confirmar baja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
